import React, { useCallback, useMemo } from 'react';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import DeleteIcon from '@mui/icons-material/Delete';
import RestoreIcon from '@mui/icons-material/Restore';
import {
  Checkbox,
  TextInput,
  SelectMeta,
  DatePicker,
} from '@/common/components/Inputs/index';
import styles from '@/common/containers/FormConstructor/styles.less';
import ConfirmModal from '@/common/containers/Modals/ConfirmModal/index';
import { useTranslation } from 'react-i18next';
import { useLocalizedField } from '@/common/hooks/index';
import { formatInputDate } from '@/common/utils/index';
import { Button, Typography } from '@mui/material';
import { useFormContext } from 'react-hook-form';

const hasMeaningfulValue = (value) => {
  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (typeof value === 'boolean') {
    return value;
  }

  if (value && typeof value === 'object') {
    return Object.values(value).some(hasMeaningfulValue);
  }

  return value !== '' && value !== null && value !== undefined;
};

const InputComponent = ({ input: { type }, ...props }) => {
  switch (type) {
    case 'number':
    case 'text': {
      return <TextInput {...props} type={type} />;
    }
    case 'checkbox': {
      return <Checkbox {...props} />;
    }
    case 'date': {
      return <DatePicker {...props} />;
    }
    default: {
      if (type.includes('DD_')) {
        return <SelectMeta metaName={type} {...props} />;
      }

      return null;
    }
  }
};

const DataEntryForm = ({
  header,
  parentKey,
  properties = [],
  number,
  can_add_new,
  canDelete,
  onDelete,
  canRestore,
  deleteType = 'delete',
  isArchived = false,
  listItemName,
  listItemDeleteText,
  onRestore,
  value,
  headerFr,
  listItemNameFr,
}) => {
  const { t } = useTranslation();
  const getLocalizedField = useLocalizedField();
  const formContext = useFormContext();
  const formValues = formContext?.getValues?.(`${parentKey}.value`) || {};
  const shouldSkipDeleteConfirmation =
    deleteType === 'delete' && !hasMeaningfulValue(formValues);
  // splits properties into columns
  const formattedList = useMemo(() => {
    const columned = properties.reduce((acc, item) => {
      const column = acc[item.row] || [];
      if (!acc[item.row]) acc[item.row] = column;
      column.push(item);

      return acc;
    }, {});

    return Object.values(columned);
  }, [properties]);

  const renderRow = useCallback(
    (row) =>
      row.map((input) => {
        const { colWidth, key, isRequired, disabled, mask, minDate } = input;
        const localizedLabel = getLocalizedField(input, 'label');
        const localizedPlaceholder = getLocalizedField(input, 'placeholder');
        const name = parentKey ? `${parentKey}.value.${key}` : key;
        const archivedValue = value?.[key];

        if (isArchived) {
          if (input.type === 'checkbox') {
            return (
              <Grid key={key} size={colWidth}>
                <Checkbox
                  checked={!!archivedValue}
                  disabled
                  label={localizedLabel}
                  name={name}
                  withForm={false}
                />
              </Grid>
            );
          }

          const disabledValue =
            input.type === 'date' && archivedValue
              ? formatInputDate(archivedValue)
              : archivedValue || '';

          return (
            <Grid key={key} size={colWidth}>
              <TextField
                disabled
                fullWidth
                label={localizedLabel}
                name={name}
                value={disabledValue}
                placeholder={localizedPlaceholder}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          );
        }

        const props = {
          label: localizedLabel,
          name,
          required: isRequired,
          disabled,
          placeholder: localizedPlaceholder,
          mask,
          rules: {
            required: isRequired
              ? t('Please fill {{label}}', { label: localizedLabel })
              : false,
            maxLength: 500,
          },
        };
        if (minDate) props.minDate = minDate;

        return (
          <Grid key={key} size={colWidth}>
            <InputComponent input={input} {...props} />
          </Grid>
        );
      }),
    [getLocalizedField, isArchived, parentKey, t, value]
  );

  const localizedHeader =
    getLocalizedField({ header, headerFr }, 'header') || header;
  const localizedListItemName =
    getLocalizedField({ listItemName, listItemNameFr }, 'listItemName') ||
    listItemName;
  const modalItemLabel = localizedListItemName || localizedHeader;
  const requiredValuesTitle = properties
    .filter(
      ({ isRequired, key }) => isRequired && (value?.[key] || formValues?.[key])
    )
    .map(({ key }) => value?.[key] || formValues?.[key])
    .join(', ');

  const headerBaseText = can_add_new
    ? `${localizedListItemName} ${number}`
    : localizedHeader;
  const headerText = isArchived
    ? t('Archived {{item}}', { item: headerBaseText })
    : headerBaseText;

  const deleteDescription =
    deleteType === 'archive'
      ? t('dataEntry.archiveDeleteDescription')
      : t('dataEntry.permanentDeleteDescription');
  const deleteTitle =
    deleteType === 'archive'
      ? requiredValuesTitle
        ? t('dataEntry.archiveDeleteTitleWithValues', {
            item: modalItemLabel,
            values: requiredValuesTitle,
          })
        : t('dataEntry.archiveDeleteTitle', {
            item: modalItemLabel,
          })
      : t('dataEntry.permanentDeleteTitle', {
          item: modalItemLabel?.toLowerCase?.() || modalItemLabel,
        });

  return (
    <div className={styles.dataEntry}>
      {headerText && (
        <div className={styles.dataEntryHeader}>
          <Typography variant="subtitle1">{headerText}</Typography>
          {canRestore && (
            <Button
              color="primary"
              size="medium"
              variant="text"
              startIcon={<RestoreIcon />}
              onClick={onRestore}
            >
              {t('Restore')}
            </Button>
          )}
          {canDelete &&
            (shouldSkipDeleteConfirmation ? (
              <Button
                color="error"
                size="medium"
                variant="text"
                startIcon={<DeleteIcon />}
                onClick={onDelete}
              >
                {t('Delete')}
              </Button>
            ) : (
              <ConfirmModal
                confirmText={t('delete')}
                title={deleteTitle}
                description={deleteDescription}
                type="delete"
              >
                {(confirm) => (
                  <Button
                    color="error"
                    size="medium"
                    variant="text"
                    startIcon={<DeleteIcon />}
                    onClick={confirm(onDelete)}
                  >
                    {t('Delete')}
                  </Button>
                )}
              </ConfirmModal>
            ))}
        </div>
      )}
      {formattedList.map((row, ind) => (
        <Grid key={ind} className={styles.dataEntryItem} spacing={4} container>
          {renderRow(row)}
        </Grid>
      ))}
    </div>
  );
};

export default DataEntryForm;
